import { apiClient } from './axios';

export interface Booking {
  id: string;
  businessId: string;
  locationId: string | null;
  petId: string | null;
  petName: string;
  petSpecies: string | null;
  petBreed: string | null;
  ownerName: string;
  ownerEmail: string | null;
  ownerPhone: string | null;
  ownerClerkId: string | null;
  scheduledDate: string;
  scheduledTime: string;
  duration: number | null;
  endTime: string | null;
  serviceType: string | null;
  serviceName: string | null;
  serviceCategory: string | null;
  reasonKey: string | null;
  reason: string | null;
  notes: string | null;
  assignedToMemberId: string | null;
  assignedAt: string | null;
  assignedBy: string | null;
  status: BookingStatus;
  priority: string;
  source: string;
  dataShared: string[] | null;
  completedAt: string | null;
  completedBy: string | null;
  completionNotes: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  estimatedPrice: number | null;
  finalPrice: number | null;
  isEmergency: boolean;
  checkedInAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  // Joined fields
  businessName?: string;
  businessPhone?: string;
  businessEmail?: string;
}

export type BookingStatus =
  | 'new'
  | 'pending_assignment'
  | 'assigned'
  | 'confirmed'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'no_show'
  | 'rescheduled';

export interface StatusHistoryEntry {
  id: string;
  bookingId: string;
  fromStatus: string | null;
  toStatus: string;
  changedBy: string;
  reason: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface BookingListResponse {
  bookings: Booking[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface BookingDetailResponse {
  booking: Booking;
  statusHistory: StatusHistoryEntry[];
}

const bookingsApi = {
  /** List current user's bookings */
  async list(params?: { status?: string; page?: number; limit?: number }): Promise<BookingListResponse> {
    const { data } = await apiClient.get('/bookings/mine', { params });
    return data;
  },

  /** Get booking detail with status history */
  async getById(bookingId: string): Promise<BookingDetailResponse> {
    const { data } = await apiClient.get(`/bookings/${bookingId}`);
    return data;
  },

  /** Create a new booking */
  async create(booking: {
    businessId: string;
    locationId?: string;
    petId?: string;
    petName: string;
    petSpecies?: string;
    petBreed?: string;
    ownerName: string;
    ownerEmail?: string;
    ownerPhone?: string;
    scheduledDate: string;
    scheduledTime: string;
    duration?: number;
    serviceType?: string;
    serviceName?: string;
    serviceCategory?: string;
    reasonKey?: string;
    reason?: string;
    notes?: string;
    isEmergency?: boolean;
    dataShared?: string[];
  }): Promise<{ message: string; booking: Booking }> {
    const { data } = await apiClient.post('/bookings', booking);
    return data;
  },

  /** Cancel a booking */
  async cancel(bookingId: string, reason?: string): Promise<{ message: string; status: string }> {
    const { data } = await apiClient.patch(`/bookings/${bookingId}/cancel`, { reason });
    return data;
  },
};

export default bookingsApi;
